import AccountLayout from '../../components/layout/account-layout'
import IconBox from '../../components/common/icon-box'
import { toast } from 'react-toastify'
import { useUser } from '../../common/hooks/auth/useUser'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import Toggle from '../../components/common/toggle'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Modal from '../../components/common/modal/modal'
import TwoFactorAuthentication from '../auth/2fa'
import Button from '../../components/common/button'
import { TwoFaDto } from '@ringo/shared'
import { useIamApi } from '../../common/api-context'
import { useTranslation } from '../../useTranslation'
import I18nText from '../../components/common/i18n-text'

enum InitTypes {
    ENABLE = 'ENABLE',
    DISABLE = 'DISABLE',
}
const Settings = () => {
    const { user, refreshUserData } = useUser()
    const [showModal, setShowModal] = useState(false)
    const [initType, setInitType] = useState<InitTypes>()
    const [twoFaCode, setTwoFaCode] = useState<string>()
    const iamApi = useIamApi()

    const { t } = useTranslation()

    const InitEnable2FaEmail = useMutation({
        mutationFn: () => {
            return iamApi.initEnable2FaEmail()
        },
        onSuccess: () => {
            setInitType(InitTypes.ENABLE)
            setShowModal(true)
            toast.success(t('myAccount.security.2FATokenSentSuccess'))
        },
        onError: () => {
            toast.error(t('myAccount.security.2FATokenSentError'))
        },
    })

    const enable2FaEmail = useMutation({
        mutationFn: () => {
            return iamApi.enable2FaEmail({
                twoFaCode: twoFaCode,
            } as TwoFaDto)
        },
        onSuccess: () => {
            refreshUserData()
            setShowModal(false)
            setInitType(undefined)
            toast.success(t('myAccount.security.2FaEnableSuccess'))
        },
        onError: () => {
            toast.error(t('myAccount.security.2FaEnableError'))
        },
    })

    const InitDisable2FaEmail = useMutation({
        mutationFn: () => {
            return iamApi.initDisable2FaEmail()
        },
        onSuccess: () => {
            setInitType(InitTypes.DISABLE)
            setShowModal(true)
            toast.success(t('myAccount.security.2FATokenSentSuccess'))
        },
        onError: () => {
            toast.error(t('myAccount.security.2FATokenSentError'))
        },
    })

    const disable2FaEmail = useMutation({
        mutationFn: () => {
            return iamApi.disable2FaEmail({
                twoFaCode: twoFaCode,
            } as TwoFaDto)
        },
        onSuccess: () => {
            refreshUserData()
            setShowModal(false)
            setInitType(undefined)
            toast.success(t('myAccount.security.2FaDisableSuccess'))
        },
        onError: () => {
            toast.error(t('myAccount.security.2FaDisableError'))
        },
    })

    const resendCode = () => {
        if (initType === InitTypes.ENABLE) {
            InitEnable2FaEmail.mutate()
        } else {
            InitDisable2FaEmail.mutate()
        }
    }

    const update2Fa = (val: boolean) => {
        if (val) {
            InitEnable2FaEmail.mutate()
        } else {
            InitDisable2FaEmail.mutate()
        }
    }

    const handle2FaSubmit = () => {
        if (initType === InitTypes.ENABLE) {
            enable2FaEmail.mutate()
        } else {
            disable2FaEmail.mutate()
        }
    }

    return (
        <AccountLayout>
            <Modal open={showModal} onClose={() => setShowModal(false)} title={t('myAccount.security.enterVerificationCode')}>
                <p className="mt-6 mb-2">
                    <I18nText
                        i18nKey="myAccount.enter2faFromEmail"
                        values={{
                            email: user?.email,
                        }}
                        components={{
                            b: <b />,
                        }}
                    />
                </p>
                <TwoFactorAuthentication
                    onChange={(val) => setTwoFaCode(val as string)}
                    onResendCode={resendCode}
                    error={enable2FaEmail.isError || disable2FaEmail.isError ? (t('myAccount.security.invalid2faToken') as string) : ''}
                />
                <Button
                    text={t('confirm')}
                    type={['squareYellow', 'full']}
                    onClick={handle2FaSubmit}
                    isLoading={enable2FaEmail.isPending || disable2FaEmail.isPending}
                    disabled={twoFaCode && twoFaCode.length < 6 ? true : false}
                />
            </Modal>
            <div className="w-3/5 max-tablet:w-9/12">
                <IconBox icon={'money-bills'}>
                    <form className="pt-12 pb-6 px-2 max-tablet:pt-10 text-left text-base">
                        <h2 className="text-xl font-bold leading-none max-tablet:text-base">{t('myAccount.security.twoFactorAuthentication')}</h2>
                        <p className="mt-1.5 mb-6 text-base max-tablet:text-sm max-tablet:mt-1">{t('myAccount.security.twoFactorAuthenticationDescription')}</p>
                        {!initType ? (
                            <Toggle
                                label={t('myAccount.security.email2fa')}
                                value={user?.email2FaActivated as boolean}
                                onChange={(val) => update2Fa(val as boolean)}
                            />
                        ) : (
                            <div className="text-base">
                                <div className="flex justify-between">
                                    <span className="text-gray-300">{t('myAccount.security.enterVerificationCode')}</span>
                                    <span className="text-gray-300">{t('myAccount.security.waitingForConfirmation')}</span>
                                </div>
                                <p className="mt-3">
                                    <I18nText
                                        i18nKey="myAccount.verificationCodeSentToEmail"
                                        values={{
                                            email: user?.email,
                                        }}
                                        components={{
                                            b: <b />,
                                        }}
                                    />
                                </p>
                                <p className="mt-4 italic">{t('myAccount.security.codeExpiresIn5Minuts')}</p>

                                <p className="mt-[50px] text-custom_yellow cursor-pointer hover:underline" onClick={() => setShowModal(true)}>
                                    {t('myAccount.security.verifyNow')}
                                    <FontAwesomeIcon icon={['fas', 'right-long']} className="pl-2 text-sm" />
                                </p>
                            </div>
                        )}
                    </form>
                </IconBox>
            </div>
        </AccountLayout>
    )
}

export default Settings
